/**
 * Reporting — module 06, Part A (`05-reporting-marking.md` §A1–§A5).
 *
 * The panel-wide status and navigation layer: the `Report Progress` band, §A3's three
 * progress stat boxes, and §A4's two roster panels with a `Report Status` pill on every row.
 * The marking itself and the report chain live on each official's own screen, reached from a
 * row — this page says where the whole panel stands and gets the TD to the one he wants.
 *
 * **§A1's stacked layout, in order:** the `Report Progress` band · the `.statgrid` · the
 * `Technical Panel` band and its `Technical Officials` panel · the `Umpiring Panel` band and
 * its `Umpiring Officials` panel.
 *
 * **One fetch, on purpose.** `GET /reports/progress` returns the denominator, the counts and
 * the rows from a single snapshot, because §A3 requires the boxes to be unable to drift from
 * the pills beneath them. A roster call plus a report call could show a percentage that the
 * pills below it do not add up to.
 *
 * **§B9's loading rule is obeyed literally:** *"never render a 0.0% stat box while loading"*.
 * While the fetch is in flight there are no boxes at all, only skeletons — a 0.0% would be a
 * figure the page does not yet have.
 *
 * **Who is listed (rulings R2′ and R1′).** Everybody except the TD, who receives no report
 * and no marks and is never counted, never listed and never rendered as a `Blank` row; and
 * one row per official for the whole tournament, whichever competitions they worked. The
 * server decides both, so this screen cannot quietly disagree with the denominator.
 *
 * **`Push All Ready To TMS` (the TD's request of 2 October 2026)** sits in the Technical Officials
 * panel's head: every TO, Judge and ATD whose report is `TD Drafted` with every mark set, pushed
 * through the TMS Push extension behind ONE Confirm (`PushAllReady.tsx`). The pills are read again
 * when a batch ends, so a pushed official's pill moves to `TMS Draft` on this page at once.
 */

import { useCallback, useState } from "react";

import { REPORT_STAGES, type OfficialRole } from "@hockeytd/shared";

import { fetchReportProgress } from "../api/client.js";
import type { ReportProgressBody } from "../api/types.js";
import { TOURNAMENT_REPORT_URL } from "../app/modules.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { FailBanner, Skeleton } from "../app/states.js";
import { useAsync } from "../app/useAsync.js";
import { RosterBand } from "../features/officials/RosterBand.js";
import { ProgressStatBoxes } from "../features/reporting/ProgressStatBoxes.js";
import { PushAllReadyButton, PushAllReadyReport, useReportBatch } from "../features/reporting/PushAllReady.js";
import { PipelineKey } from "../features/reporting/ReportStatusPill.js";
import { ROSTER_PANEL_NOTE, RosterPanel } from "../features/reporting/RosterPanel.js";

const TECHNICAL_ROLES: readonly OfficialRole[] = ["ATD", "TO", "Judge"];
const UMPIRING_ROLES: readonly OfficialRole[] = ["UM", "Umpire"];

export function ReportingPage(): React.JSX.Element {
  const [reads, setReads] = useState(0);
  const state = useAsync<ReportProgressBody>(fetchReportProgress, [reads]);
  const reread = useCallback(() => {
    setReads((count) => count + 1);
  }, []);
  const batch = useReportBatch(reread);
  const [technicalRole, setTechnicalRole] = useState<OfficialRole | null>(null);
  const [umpiringRole, setUmpiringRole] = useState<OfficialRole | null>(null);

  const officials = state.status === "ready" ? state.data.officials : [];
  const technical = officials.filter((official) => TECHNICAL_ROLES.includes(official.role));
  const umpiring = officials.filter((official) => UMPIRING_ROLES.includes(official.role));

  return (
    <>
      <div className="repband">
        <h2>
          Report Progress{" "}
          {state.status === "ready" ? (
            <span className="md">· All {state.data.denominator} Officials</span>
          ) : null}
        </h2>
        <HowToUse id="reporting" title="How to use Report Progress">
          <li>
            The three boxes at the top —{" "}
            <HowToLink to="progress-boxes">Reports Drafted, Reports Submitted and TMS
            Published</HowToLink> — count the whole panel. The denominator is every official on
            the roster, so the share cannot be flattered by leaving somebody out.
          </li>
          <li>
            <HowToLink to="tech-band">Technical Panel</HowToLink> lists the technical
            officials. Each row&rsquo;s status chip shows the furthest stage that
            person&rsquo;s report has reached.
          </li>
          <li>
            <HowToLink to="ump-band">Umpiring Panel</HowToLink> lists the umpires and
            umpires managers the same way.
          </li>
          <li>
            Click any name to open that official and do the work. One report and one set of
            marks per person, for the whole tournament — not per match.
          </li>
          <li>
            <b>TMS Published</b> is read back from TMS. This system never sets it, because
            publishing is a deliberate step inside TMS.
          </li>
          <li>
            <HowToLink to="tournament-report">Tournament Report</HowToLink> opens the
            federation&rsquo;s own tournament report form in a new tab. That one is yours as
            Technical Delegate and is separate from the per-official reports on this page.
          </li>
        </HowToUse>
        <span className="spacer" style={{ flex: 1 }} />
        {/*
          The mockup's own band caption — it says WHERE the work is done, which is the one
          thing a status-and-navigation page has to answer before the reader starts looking
          for a text box that is not on it.
        */}
        <p className="rosterband__count" data-testid="reportingBandCount">
          Written On Each Official&rsquo;s Profile · <b>Reporting Section</b>
        </p>
      </div>

      {state.status === "loading" ? <Skeleton rows={3} /> : null}
      {state.status === "error" ? <FailBanner error={state.error} /> : null}

      {state.status === "ready" ? (
        <>
          <ProgressStatBoxes
            counts={state.data.counts}
            denominator={state.data.denominator}
          />

          {/*
            The same band component the officials roster uses, because the two mockups draw
            the identical band: a heading with a thin second half and a count line on the
            right whose every figure is counted from the rows the panel below actually holds.
            The Reporting page previously drew a bare `<h2>Technical Panel</h2>` with neither.
          */}
          <RosterBand
            id="tech"
            title="Technical Panel"
            subtitle="ATDs, Technical Officers And Judges"
            officials={technical}
            roles={TECHNICAL_ROLES}
          />

          <RosterPanel
            id="tech"
            title="Technical Officials"
            note={ROSTER_PANEL_NOTE}
            tabsAriaLabel="Technical role filter"
            roles={TECHNICAL_ROLES}
            officials={technical}
            selectedRole={technicalRole}
            onSelectRole={setTechnicalRole}
            headAction={<PushAllReadyButton controller={batch} />}
            beneathHead={<PushAllReadyReport controller={batch} />}
          >
            <PipelineKey stages={REPORT_STAGES} />
            <p>
              <b>Report Status shows the furthest stage reached.</b> The report itself is
              written on the official&apos;s own profile page.
            </p>
            <p>
              <b>Cap counts come from TMS and the unit differs by role —</b> for{" "}
              <b>Technical Officers and Judges</b> it is the number of matches officiated at
              the technical table; for <b>Umpires</b> it is the number of matches umpired; and
              for <b>Umpires Managers</b> it is the number of <b>tournaments</b> served as UM,
              not matches. Hover any Cap figure to see its unit.
            </p>
          </RosterPanel>

          <RosterBand
            id="ump"
            title="Umpiring Panel"
            subtitle="Umpires Managers And Umpires"
            officials={umpiring}
            roles={UMPIRING_ROLES}
          />

          <RosterPanel
            id="ump"
            title="Umpiring Officials"
            note={ROSTER_PANEL_NOTE}
            tabsAriaLabel="Umpiring role filter"
            roles={UMPIRING_ROLES}
            officials={umpiring}
            selectedRole={umpiringRole}
            onSelectRole={setUmpiringRole}
          >
            <p>
              <b>Report Status shows the furthest stage reached.</b> The report itself is
              written on the official&apos;s own profile page.
            </p>
            <p>
              <b>Umpires Managers are counted in tournaments —</b> a UM cap of <b>18</b> means
              eighteen tournaments served as Umpires Manager, which is why the figures look
              small beside an umpire&rsquo;s match count.
            </p>
            <p>
              <b>Every umpire row is owned by an umpires manager.</b> She writes that report and
              sends it. Your part is the agreed marks and your closing remarks.
            </p>
          </RosterPanel>
        </>
      ) : null}

      {/*
        The FIH form link lives at the very foot of this page (Alfred, 2026-09-03): it is
        filled in exactly once, after the closing match, so it sits below the working
        panels rather than in the heading band — findable at the end, never in the way.
      */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 28 }}>
        <a
          className="linkbtn-ext"
          href={TOURNAMENT_REPORT_URL}
          target="_blank"
          rel="noopener"
          data-howto="tournament-report"
        >
          Tournament Report <span aria-hidden="true">↗</span>
        </a>
      </div>
    </>
  );
}
